import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { IMAGES } from "@/lib/site";
import { ArrowUpRight } from "lucide-react";

const values = [
  { n: "01", t: "Integrity", d: "Honest, evidence-based commercial analysis." },
  { n: "02", t: "Commercial Discipline", d: "Decisions grounded in net recovered value." },
  { n: "03", t: "Compliance", d: "Duty of Care at the centre of every movement." },
  { n: "04", t: "Transparency", d: "Clear commercial logic, not opaque contracts." },
  { n: "05", t: "Resource Efficiency", d: "Recoverable fibre kept in productive use." },
];

export default function About() {
  const navigate = useNavigate();
  return (
    <>
      <section className="relative pt-36 pb-24 px-4 sm:px-6 lg:px-8 overflow-hidden border-b border-dc-border">
        <div className="absolute inset-0 z-0">
          <img src={IMAGES.hauliers} alt="Articulated logistics" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-dc-bg/75" />
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto">
          <span className="dc-label">About DES-CARTA LTD</span>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="font-display uppercase tracking-tighter text-4xl sm:text-6xl lg:text-7xl font-semibold leading-[0.92] mt-6 max-w-5xl">
            Independent Brokerage for the Recovered Materials Economy
          </motion.h1>
        </div>
      </section>

      <section className="py-16 lg:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 space-y-6 text-dc-text2 leading-relaxed text-lg">
            <Reveal><p>DES-CARTA LTD was established to provide businesses with an independent route into the recovered-materials market.</p></Reveal>
            <Reveal delay={0.05}><p>Rather than relying on owned vehicles, yards or processing infrastructure, the company focuses on brokerage, market access, logistics coordination and commercial optimisation.</p></Reveal>
            <Reveal delay={0.1}><p>This asset-light structure allows the business to evaluate appropriate solutions across the market rather than directing material toward infrastructure it owns.</p></Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="border border-dc-border bg-dc-surface p-8">
                <div className="dc-label mb-4">Our Mission</div>
                <p className="font-display text-2xl leading-snug">To make commercial recycling more transparent, competitive and commercially intelligent.</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-dc-surface/40 border-y border-dc-border">
        <div className="max-w-[1400px] mx-auto">
          <span className="dc-label">Values</span>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-px bg-dc-border border border-dc-border">
            {values.map((v, i) => (
              <Reveal key={v.n} delay={i * 0.06}>
                <div className="bg-dc-bg p-7 h-full">
                  <div className="font-display text-3xl text-dc-accent mb-4">{v.n}</div>
                  <h3 className="font-medium text-white mb-2">{v.t}</h3>
                  <p className="text-dc-text2 text-xs leading-relaxed">{v.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-[1400px] mx-auto flex flex-col lg:flex-row justify-between items-start lg:items-center gap-8">
          <h2 className="font-display uppercase tracking-tight text-3xl lg:text-5xl font-semibold leading-[0.95] max-w-2xl">Ready to Benchmark Your Recovered-Material Arrangement?</h2>
          <button data-testid="about-cta" onClick={() => navigate("/contact")} className="inline-flex items-center gap-2 bg-dc-accent text-dc-bg font-semibold px-7 py-4 hover:bg-dc-accentHover transition-colors shrink-0">Request a Waste Value Assessment <ArrowUpRight size={18} /></button>
        </div>
      </section>
    </>
  );
}
